import { CheckCircle2, Circle, Target } from "lucide-react";
import { CourseCard } from "./CourseCard";
import { LEVEL_CLS } from "./useAcademy";
import { cn } from "@/lib/utils";

function GapChip({ comp, onAssess }) {
  if (!comp.self_level) return (
    <button onClick={onAssess} data-testid={`gap-${comp.key}`} className="mt-2 text-[11px] text-slate-500 underline decoration-dotted hover:text-emerald-700">Not self-assessed — rate yourself</button>
  );
  const tone = comp.gap > 1 ? "bg-rose-50 text-rose-700 border-rose-200" : comp.gap === 1 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200";
  return (
    <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid={`gap-${comp.key}`}>
      <span className={cn("px-2 py-0.5 rounded-md border text-[11px]", LEVEL_CLS[comp.self_level])}>Self: {comp.self_level_label}</span>
      <span className={cn("px-2 py-0.5 rounded-md border text-[11px] font-medium", tone)}>{comp.gap_label}</span>
    </div>
  );
}

function CompetencyRow({ comp, actions, index, onAssess }) {
  const pct = comp.target > 0 ? Math.min(100, Math.round((comp.completed_count / comp.target) * 100)) : 0;
  return (
    <section data-testid={`competency-${comp.key}`} className={cn("bg-white rounded-2xl border shadow-sm overflow-hidden animate-fade-up", comp.gap > 0 ? "border-amber-200" : "border-slate-200/80")} style={{ animationDelay: `${index * 60}ms` }}>
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr]">
        <div className="p-5 lg:p-6 border-b lg:border-b-0 lg:border-r border-slate-100 bg-slate-50/40">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[11px] font-mono text-slate-400">0{index + 1}</span>
            <span data-testid={`required-level-${comp.key}`} className={cn("px-2 py-0.5 rounded-md border text-[11px] font-medium", LEVEL_CLS[comp.required_level])}>Required: {comp.required_level_label}</span>
          </div>
          <h3 className="font-display font-semibold text-slate-900 leading-snug">{comp.name}</h3>
          <GapChip comp={comp} onAssess={onAssess} />
          {comp.self_note && <div className="text-[11px] text-slate-500 italic mt-1" data-testid={`self-note-${comp.key}`}>“{comp.self_note}”</div>}
          <p className="text-sm text-slate-600 mt-2 leading-relaxed" data-testid={`good-looks-like-${comp.key}`}>
            <span className="text-slate-400 text-xs uppercase tracking-wider font-medium block mb-0.5">What good looks like</span>
            {comp.good_looks_like || comp.description}
          </p>
          <div className="mt-4" data-testid={`progress-${comp.key}`}>
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="text-slate-500 flex items-center gap-1"><Target className="w-3.5 h-3.5" /> Progress toward next stage</span>
              <span className={cn("font-mono", comp.on_track ? "text-emerald-600" : "text-slate-500")}>{comp.completed_count}/{comp.target || "–"}</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className={cn("h-full rounded-full transition-all", comp.on_track ? "bg-emerald-500" : "bg-emerald-400/70")} style={{ width: `${pct}%` }} /></div>
            <div className="mt-1.5 text-[11px] flex items-center gap-1">
              {comp.on_track ? <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /><span className="text-emerald-700">On track</span></>
                : comp.target === 0 ? <><Circle className="w-3.5 h-3.5 text-slate-300" /><span className="text-slate-400">No trackable courses yet</span></>
                  : <><Circle className="w-3.5 h-3.5 text-slate-300" /><span className="text-slate-500">Complete {comp.target - comp.completed_count} more</span></>}
            </div>
          </div>
        </div>
        <div className="p-4 lg:p-5">
          {comp.recommended.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-400 py-10 border border-dashed border-slate-200 rounded-xl">No approved courses for this stage yet.</div>
          ) : (
            <div className="flex gap-4 overflow-x-auto pb-2 snap-x" data-testid={`recommended-${comp.key}`}>
              {comp.recommended.map((c, i) => <CourseCard key={c.id} c={c} actions={actions} compact index={i} />)}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export function CompetencyMap({ competencies, actions, onAssess }) {
  return (
    <div className="space-y-5" data-testid="competency-map">
      {competencies.map((comp, i) => <CompetencyRow key={comp.key} comp={comp} actions={actions} index={i} onAssess={onAssess} />)}
    </div>
  );
}
